import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import { isEmployer, useJobBankRole } from "./jobBankRole";
import PublicProfileCard from "./PublicProfileCard";
import "./PublicProfiles.css";

function getCompanyName(profile) {
  return profile.companyName || profile.organizationName || "Employer";
}

function getLocation(profile) {
  return [profile.city, profile.province].filter(Boolean).join(", ");
}

function EmployerListItem({ employer, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`employerDirectoryListItem${selected ? " selected" : ""}`}
      onClick={() => onSelect(employer)}
    >
      <h3>{getCompanyName(employer)}</h3>
      <p className="employerDirectoryListIndustry">{employer.industry || "Employer"}</p>
      {getLocation(employer) && (
        <p className="employerDirectoryListLocation">{getLocation(employer)}</p>
      )}
    </button>
  );
}

function EmployerDetailPanel({ employer, canEnterPortal, navigate }) {
  if (!employer) {
    return (
      <div className="publicProfileEmpty employerDirectoryDetailEmpty">
        Select an employer to view their profile.
      </div>
    );
  }

  return (
    <PublicProfileCard
      title={getCompanyName(employer)}
      subtitle={employer.industry || "Employer"}
      description={employer.companyDescription || ""}
      details={[
        { label: "Location", value: getLocation(employer) },
        { label: "Website", value: employer.website },
        { label: "Hiring Regions", value: employer.hiringRegions },
        { label: "Remote Available", value: employer.remoteAvailable ? "Yes" : "No" },
        { label: "Primary Contact", value: employer.contactName },
        { label: "Email", value: employer.email }
      ]}
      actions={
        <>
          <button type="button" onClick={() => navigate("/JobBankJobs")}>Browse Jobs</button>
          <button type="button" className="secondary" onClick={() => navigate("/careercoaches")}>
            Find a Career Coach
          </button>
          {canEnterPortal && (
            <button type="button" className="secondary" onClick={() => navigate("/employer")}>
              Enter Employer Portal
            </button>
          )}
        </>
      }
    />
  );
}

export default function PublicEmployerDirectory() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { role, roleLoading } = useJobBankRole(currentUser);
  const [employers, setEmployers] = useState([]);
  const [selectedId, setSelectedId] = useState(uid || null);
  const [loading, setLoading] = useState(true);

  const canEnterPortal = isEmployer(role);

  const loadEmployers = useCallback(async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(query(collection(db, "employers"), orderBy("companyName")));
      setEmployers(snapshot.docs.map(employerDoc => ({ id: employerDoc.id, ...employerDoc.data() })));
    } catch (error) {
      console.error("Unable to load employer profiles:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!roleLoading) {
      loadEmployers();
    }
  }, [loadEmployers, roleLoading]);

  useEffect(() => {
    if (!employers.length) return;

    if (uid && employers.some(employer => employer.id === uid)) {
      setSelectedId(uid);
      return;
    }

    if (uid && !employers.some(employer => employer.id === uid)) {
      setSelectedId(null);
      return;
    }

    if (!selectedId || !employers.some(employer => employer.id === selectedId)) {
      setSelectedId(employers[0].id);
    }
  }, [employers, uid, selectedId]);

  const selectedEmployer = useMemo(
    () => employers.find(employer => employer.id === selectedId) || null,
    [employers, selectedId]
  );

  function handleSelectEmployer(employer) {
    setSelectedId(employer.id);
    navigate(`/employers/${employer.id}`, { replace: true });
  }

  if (loading || roleLoading) {
    return (
      <div className="publicDirectoryPage">
        <div className="publicProfileEmpty">Loading employer profiles...</div>
      </div>
    );
  }

  if (employers.length === 0) {
    return (
      <div className="publicDirectoryPage">
        <header className="publicDirectoryHeader">
          <h1>Employer Directory</h1>
          <p>Browse employers in the Community Job Bank.</p>
        </header>
        <div className="publicProfileEmpty">No employer profiles are listed yet.</div>
      </div>
    );
  }

  return (
    <div className="publicDirectoryPage">
      <header className="publicDirectoryHeader">
        <h1>Employer Directory</h1>
        <p>Browse employers in the Community Job Bank. Select an employer to view their profile.</p>
      </header>

      <div className="employerDirectoryLayout">
        <aside className="employerDirectoryList" aria-label="Employer list">
          {employers.map(employer => (
            <EmployerListItem
              key={employer.id}
              employer={employer}
              selected={employer.id === selectedId}
              onSelect={handleSelectEmployer}
            />
          ))}
        </aside>

        <section className="employerDirectoryDetail" aria-label="Employer profile">
          {uid && !selectedEmployer ? (
            <div className="publicProfileEmpty employerDirectoryDetailEmpty">
              Employer profile not found.
            </div>
          ) : (
            <EmployerDetailPanel
              employer={selectedEmployer}
              canEnterPortal={canEnterPortal}
              navigate={navigate}
            />
          )}
        </section>
      </div>
    </div>
  );
}
